import { Form, SpaceBetween, SelectProps, SpaceBetweenProps } from "@amzn/awsui-components-react";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { CUSTOM_FORM_DEFAULT_VALUES, CustomFormFieldTypes, CustomFormProps } from "interfaces";
import React, { Fragment, JSX, useState } from "react";
import { CustomFormField } from "interfaces";

import CustomDateFormField from "../CustomFormField/CustomDateFormField";
import CustomFileDropzone from "../CustomFormField/CustomFileDropzone";
import CustomSelectFormField from "../CustomFormField/CustomSelectFormField";
import CustomTextFormField from "../CustomFormField/CustomTextFormField";
import CustomToggleFormField from "../CustomFormField/CustomToggleFormField";
import CustomArrayInput from "../CustomFormField/CustomArrayInput";

/**
 * A function retrieves the default value of an input depending on the variant
 * If the customForm receives a default value, it will return that value instead
 *
 * @param {CustomFormField} singleForm - The id of the hardware to retrieve the position
 *
 * @returns {CustomFormFieldTypes} the default value depending on the variant
 */
export const getDefaultValue = (singleForm: CustomFormField): CustomFormFieldTypes => {
    if (singleForm.defaultValue !== undefined) return singleForm.defaultValue;

    return CUSTOM_FORM_DEFAULT_VALUES[singleForm.variant];
};

/**
 * A function that creates the value and setValue for a single form
 *
 * @param {CustomFormField} singleForm
 * @param {Map<string, CustomFormFieldTypes>} values
 * @param {React.Dispatch<React.SetStateAction<Map<string, CustomFormFieldTypes>>>} setValues
 * @param {CustomFormFieldTypes} defaultValue
 *
 * @returns {object} The value and setValue for a single form
 */
export const getSetter = (
    singleForm: CustomFormField,
    values: Map<string, CustomFormFieldTypes>,
    setValues: React.Dispatch<React.SetStateAction<Map<string, CustomFormFieldTypes>>>,
    defaultValue: CustomFormFieldTypes,
) => {
    const setValue = (val: CustomFormFieldTypes) =>
        setValues((prevValues) => {
            const newValues = new Map(prevValues);
            newValues.set(singleForm.id, val);
            return newValues;
        });
    const value = values.get(singleForm.id) || defaultValue;

    return { value, setValue };
};

/**
 * A function that formats a single form default props in an object
 *
 * @param {CustomFormField} singleForm - The single form props
 * @param {(val: CustomFormFieldTypes) => void} setValue - The setValue for the single form
 *
 * @returns {object} The default props for a single form
 */
export const getCommonProps = (singleForm: CustomFormField, setValue: (val: CustomFormFieldTypes) => void) => {
    return {
        label: singleForm.label,
        setValue: setValue,
        errorText: singleForm.errorText,
        warningText: singleForm.warningText,
        description: singleForm.description,
        info: singleForm.info,
        placeholder: singleForm.placeholder,
        disabled: singleForm.disabled,
        testId: singleForm.testId,
        rows: singleForm.rows,
        accept: singleForm.accept,
        multiple: singleForm.multiple,
        touchpoint: singleForm.touchpoint,
        validateInput: singleForm.validateInput,
        sanitizeInput: singleForm.sanitizeInput,
    };
};

/**
 * A function to retrieve the input based on the variant selected
 *
 * @param {CustomFormField} singleForm - the single input form
 * @param {CustomFormFieldTypes} value - the value displayed in the input
 * @param {(val: CustomFormFieldTypes) => void} setValue - the function to set the value of the input
 *
 * @returns {React.ReactNode} the input based on the variant selected
 */
export const getSingleForm = (
    singleForm: CustomFormField,
    value: CustomFormFieldTypes,
    setValue: (val: CustomFormFieldTypes) => void,
) => {
    const commonProps = getCommonProps(singleForm, setValue);

    switch (singleForm.variant) {
        case "date-picker":
        case "date":
            return <CustomDateFormField variant={singleForm.variant} value={value as string} {...commonProps} />;
        case "toggle":
            return <CustomToggleFormField value={value as boolean} {...commonProps} />;
        case "select":
            const DEFAULT_OPTIONS: SelectProps.Options = [];

            return (
                <CustomSelectFormField
                    value={value as OptionDefinition}
                    options={singleForm.options || DEFAULT_OPTIONS}
                    {...commonProps}
                />
            );
        case "fileDropzone":
            return <CustomFileDropzone value={value as File[]} {...commonProps} />;
        case "array-input":
            return <CustomArrayInput value={value as string[]} {...commonProps}  />
        default:
            return <CustomTextFormField variant={singleForm.variant} value={value as string} {...commonProps} />;
    }
};

/**
 * A CustomForm component that generates a form based on its props
 *
 * @param {React.ReactNode} actions - Form actions like submit/cancel buttons
 * @param {React.ReactNode} header - Form header content
 * @param {FormSection[]} formSections - Array of form sections with headers and inputs
 * @param {Map<string, CustomFormFieldTypes>} formValues - Map of form field values
 * @param {React.Dispatch<React.SetStateAction<Map<string, CustomFormFieldTypes>>>} setFormValues - Function to update form values
 * @param {(event: React.FormEvent<HTMLFormElement>) => void} onSubmit - Form submit handler
 *
 * @returns {JSX.Element} A Form component with configured sections and fields
 */
const CustomForm = ({ actions, header, formSections, formValues, setFormValues, onSubmit }: CustomFormProps) => {
    const placementDirection: SpaceBetweenProps.Direction = "vertical";
    const spaceBetweenSections: SpaceBetweenProps.Size = "l";
    const spaceWithinASection: SpaceBetweenProps.Size = "m";

    /**
     * Type definition for the format input return type
     * Represents either a JSX Element or null when the form field should not be rendered
     */
    type formatInputType = JSX.Element | null;

    /**
     * Function that receives a singleForm an converts it into a form field
     * The form field varies depending on the variant and props received in the singleForm
     *
     * @param {CustomFormField} singleForm - The form to convert into a form field
     *
     * @return {formatInputType} - The form field as a react node
     */
    const formatSingleInput = (singleForm: CustomFormField): formatInputType => {
        const defaultValue: CustomFormFieldTypes = getDefaultValue(singleForm);

        const { value, setValue } = getSetter(singleForm, formValues, setFormValues, defaultValue);

        const displayForm = singleForm.isVisible == undefined || singleForm.isVisible;

        return displayForm ? (
            <Fragment key={singleForm.id}>{getSingleForm(singleForm, value, setValue)}</Fragment>
        ) : null;
    };

    /**
     * Function that converts multiple customForms into a form fields
     *
     * @param {CustomFormField[]} formInputs - The customForms to convert into form fields
     *
     * @returns {formatInputType[]} multiple react node or null (if the field its not rendered)
     */
    const formatFormInputs = (formInputs: CustomFormField[]): formatInputType[] => {
        return formInputs.map((singleForm) => {
            return formatSingleInput(singleForm);
        });
    };

    return (
        <form onSubmit={onSubmit}>
            <Form actions={actions} header={header}>
                <SpaceBetween direction={placementDirection} size={spaceBetweenSections}>
                    {formSections.map((section) => {
                        const inputsAsJSX = formatFormInputs(section.formInputs);

                        return (
                            <SpaceBetween direction={placementDirection} size={spaceWithinASection}>
                                {section.header}
                                {inputsAsJSX}
                            </SpaceBetween>
                        );
                    })}
                </SpaceBetween>
            </Form>
        </form>
    );
};

export default CustomForm;
